// Port of ios/Sources/ViewModels/ChannelMembersPresentation.swift.
//
// The members view's rows, and the rule about who may change them. Pure, so the two things
// that are easy to get quietly wrong are testable: the ORDER (a list of people is unreadable
// unless it is sorted the way names are read) and the PERMISSION (a button that answers 403
// is not parity).
import { swiftStringLess, userDisplayName } from './channelListDerivation';
import { maxOthers } from './peoplePickerPresentation';
import type { UserRecord } from './records';

/** The fields of a `ChannelMemberDTO` (shared/API.md §8.5) this module reads. */
export interface ChannelMemberDTO {
  userId: string;
  /** Space-separated, e.g. "channel_user channel_admin"; absent on some payloads. */
  roles?: string | null;
}

export interface Row {
  userId: string;
  displayName: string;
  username: string;
  /** Draws the "Admin" badge — `channel_admin` in the member's `roles` (shared/API.md §8.5). */
  isChannelAdmin: boolean;
  /**
   * The reader's own row: labelled, and never offers "remove" — leaving is a different verb
   * with different consequences, and it lives on the info sheet.
   */
  isSelf: boolean;
  lastPictureUpdate: number;
  /** A guest can never be made a channel admin — the server refuses — so no role control. */
  isGuest: boolean;
}

/**
 * Rows for `members`, named under the reader's own display policy. Sorted with ADMINS first and
 * then by name, case-insensitively. The reader's own row is NOT floated to the top: they know
 * they are in the channel, and moving it breaks the alphabet they are scanning. A member this
 * device cannot name yet still gets a row, under `unknownLabel`.
 */
export function rows(args: {
  members: readonly ChannelMemberDTO[];
  usersById: ReadonlyMap<string, UserRecord>;
  currentUserId: string;
  unknownLabel: string;
}): Row[] {
  const seen = new Set<string>();
  return args.members
    .filter((m) => {
      if (seen.has(m.userId)) return false;
      seen.add(m.userId);
      return true;
    })
    .map((member): Row => {
      const user = args.usersById.get(member.userId);
      return {
        userId: member.userId,
        displayName: user ? userDisplayName(user) : args.unknownLabel,
        username: user?.username ?? '',
        isChannelAdmin: isChannelAdmin(member.roles ?? ''),
        isSelf: member.userId === args.currentUserId,
        lastPictureUpdate: user?.lastPictureUpdate ?? 0,
        isGuest: hasRole(member.roles ?? '', 'channel_guest'),
      };
    })
    .sort((a, b) => {
      if (a.isChannelAdmin !== b.isChannelAdmin) return a.isChannelAdmin ? -1 : 1;
      const na = a.displayName.toLowerCase();
      const nb = b.displayName.toLowerCase();
      if (na !== nb) return swiftStringLess(na, nb) ? -1 : 1;
      return swiftStringLess(a.userId, b.userId) ? -1 : a.userId === b.userId ? 0 : 1;
    });
}

/**
 * Whether a space-separated `roles` string carries `channel_admin`. Split rather than
 * substring-matched: role names are server configuration, and a `contains` that starts being
 * wrong when someone defines `channel_administrator_readonly` fails silently and in the
 * direction that grants access.
 */
export function isChannelAdmin(roles: string): boolean {
  return hasRole(roles, 'channel_admin');
}

function hasRole(roles: string, role: string): boolean {
  return roles.split(/[ \t]/).includes(role);
}

/**
 * Whether this reader may add or remove people here (shared/API.md §8.5.1). Two sources, either one
 * enough: the reader's own channel ROLE (the default scheme puts `manage_public_channel_members` in
 * `channel_user` and the private variant in `channel_admin`), and the resolved `permissions`
 * snapshot (§26.1), which adds what the member payload cannot show — a team or system admin
 * managing a private channel they are not channel admin of, as the desktop allows (user,
 * 2026-10-12). DMs and group messages have no membership to manage.
 */
export function canManageMembers(channelType: string, ownRoles: string, permissions: ReadonlySet<string> = new Set()): boolean {
  switch (channelType) {
    case 'O':
      return true;
    case 'P':
      return isChannelAdmin(ownRoles) || permissions.has('manage_private_channel_members') || permissions.has('manage_system');
    default:
      return false;
  }
}

/**
 * Whether this reader may make someone a channel admin or take it away (shared/API.md §8.5.4):
 * `manage_channel_roles`, read from the resolved snapshot. Real channels only.
 */
export function canManageRoles(channelType: string, permissions: ReadonlySet<string>): boolean {
  return (channelType === 'O' || channelType === 'P') && (permissions.has('manage_channel_roles') || permissions.has('manage_system'));
}

/** Never the reader's own row (demoting yourself from a list of others is a trap), never a guest. */
export function offersRoleChange(row: Row, canManageRolesHere: boolean): boolean {
  return canManageRolesHere && !row.isSelf && !row.isGuest;
}

/**
 * Mattermost's group-message ceiling: 8 accounts INCLUDING the reader. Derived from
 * `PeoplePickerPresentation.maxOthers`, which counts the same number from the other side, so the
 * two cannot drift apart.
 */
export const maxGroupMembers = maxOthers + 1;

/**
 * What a GROUP MESSAGE offers where a channel offers add/remove — "I cannot manage the members of
 * a group" (user, 2026-10-02).
 *
 * A group message has no roster to edit. Its identity IS its set of people: the server keys it on
 * exactly that set (`POST /channels/group` with the full id list is idempotent, shared/API.md
 * §19.2), so "add Ana" cannot mean anything but "the conversation of these people plus Ana",
 * which is a DIFFERENT channel with its own history. Nobody can be removed from one at all.
 *
 * `freeSeats` is what is left of the ceiling. At zero, adding is not offered at all: a full group
 * can only grow by becoming a channel.
 */
export interface GroupOptions {
  freeSeats: number;
  canAddPeople: boolean;
  canConvertToChannel: boolean;
}

export function groupOptions(channelType: string, memberCount: number): GroupOptions {
  if (channelType !== 'G') return { freeSeats: 0, canAddPeople: false, canConvertToChannel: false };
  const free = Math.max(0, maxGroupMembers - memberCount);
  return {
    freeSeats: free,
    canAddPeople: free > 0,
    // Always: converting is how a full group grows, and how any group gets a membership that can
    // be edited. Any member may do it (shared/API.md §19.4).
    canConvertToChannel: true,
  };
}

/** Namespace form, for `ChannelMembersPresentation.rows(...)` call sites. */
export const ChannelMembersPresentation = {
  rows,
  isChannelAdmin,
  canManageMembers,
  canManageRoles,
  offersRoleChange,
  maxGroupMembers,
  groupOptions,
} as const;
